<template>
  <div>
    <v-row>
      <v-col cols="12" v-if="featureEnabled === false">
        <v-alert type="info" outlined dense>
          {{ $t('agent_tokens.feature_not_included') }}
        </v-alert>
      </v-col>

      <v-col cols="12" v-else class="agent-tokens">
        <!-- Шапка: главное действие, под ним — что это -->
        <div class="agent-tokens__head">
          <v-btn v-if="canIssue !== false" depressed color="primary" @click="openCreateDialog" :loading="isLoading"
            class="agent-tokens__create" data-testid="agent-tokens-create">
            <v-icon left small>mdi-plus</v-icon>
            {{ $t('agent_tokens.create_button') }}
          </v-btn>
          <p class="agent-tokens__lead text-body-2 mb-0">
            {{ $t('agent_tokens.description') }}
          </p>
        </div>
        <v-alert v-if="canIssue === false" type="info" outlined dense class="mt-4 mb-0"
          data-testid="agent-tokens-cannot-issue">
          {{ $t('agent_tokens.role_not_allowed') }}
        </v-alert>

        <!-- Подключение: адрес MCP — API региона компании (RU / EU), как его знает приложение после входа -->
        <section class="agent-tokens__connect">
          <h3 class="agent-tokens__title">{{ $t('agent_tokens.connect_title') }}</h3>
          <div class="agent-tokens__connect-row">
            <v-text-field v-if="mcpLoaded" :value="mcpUrl" :label="$t('agent_tokens.mcp_address')" readonly outlined dense
              hide-details class="agent-tokens__address" autocomplete="off" spellcheck="false"
              data-testid="agent-tokens-mcp-address">
              <template v-slot:append>
                <v-btn icon small class="agent-tokens__copy" :aria-label="$t('agent_tokens.mcp_address_copy')"
                  :title="$t('agent_tokens.mcp_address_copy')"
                  @click="copyText(mcpUrl, 'agent_tokens.mcp_address_copied')">
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </template>
            </v-text-field>
            <v-skeleton-loader v-else type="text" class="agent-tokens__address" />
            <a :href="helpUrl" target="_blank" rel="noopener" class="agent-tokens__help text-body-2">
              {{ $t('agent_tokens.help_link') }}
              <v-icon x-small color="primary">mdi-open-in-new</v-icon>
            </a>
          </div>
          <!-- FT-189 REQ-09: агенты с терминалом подключаются командой CLI из окна нового ключа -->
          <p v-if="canIssue !== false" class="text-body-2 text--secondary mt-3 mb-0" data-testid="agent-tokens-cli-hint">
            {{ $t('agent_tokens.cli_hint_tab') }}
            <a :href="helpUrl" target="_blank" rel="noopener">{{ $t('agent_tokens.cli_hint_tab_link') }}</a>
          </p>
        </section>

        <!-- Ключи -->
        <section class="agent-tokens__list">
          <h3 class="agent-tokens__title">
            {{ $t('agent_tokens.keys_title') }}
            <span v-if="activeTokensCount" class="agent-tokens__count">{{ activeTokensCount }}</span>
          </h3>
          <v-data-table
            :headers="headers"
            :items="tokens"
            :loading="isLoading"
            dense
            hide-default-footer
            disable-sort
            :items-per-page="-1"
            class="agent-tokens__table"
          >
            <template v-slot:no-data>
              <div class="agent-tokens__empty" data-testid="agent-tokens-empty">
                <v-icon color="grey lighten-1">mdi-key-outline</v-icon>
                <span>{{ $t('agent_tokens.no_tokens') }}</span>
              </div>
            </template>
            <template v-slot:item.token_last4="{ item }">
              <code>rpa_…{{ item.token_last4 }}</code>
            </template>
            <template v-slot:item.levels="{ item }">
              <div v-if="item.legacy" data-testid="agent-token-legacy">
                <v-chip x-small outlined color="warning">{{ $t('agent_tokens.legacy_badge') }}</v-chip>
                <div class="caption grey--text text--darken-1">{{ $t('agent_tokens.legacy_hint') }}</div>
              </div>
              <div v-else-if="item.levels" class="d-flex flex-wrap">
                <v-chip v-for="entry in tokenLevelChips(item)" :key="entry.area" x-small
                  :color="entry.write ? 'warning' : undefined" :outlined="!entry.write" class="mr-1 mb-1">
                  {{ entry.text }}
                </v-chip>
              </div>
              <span v-else>—</span>
            </template>
            <template v-slot:item.user="{ item }">
              {{ item.user && item.user.name ? item.user.name : '—' }}
            </template>
            <template v-slot:item.expires_at="{ item }">
              {{ item.expires_at ? formatDate(item.expires_at) : '—' }}
            </template>
            <template v-slot:item.last_used_at="{ item }">
              {{ item.last_used_at ? formatDate(item.last_used_at) : $t('agent_tokens.never_used') }}
            </template>
            <template v-slot:item.revoked_at="{ item }">
              <v-chip x-small color="error" v-if="item.revoked_at">{{ $t('agent_tokens.revoked') }}</v-chip>
              <v-chip x-small color="warning" v-else-if="isExpired(item)">{{ $t('agent_tokens.expired') }}</v-chip>
              <v-chip x-small color="success" v-else>{{ $t('agent_tokens.active') }}</v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn x-small icon color="error" v-if="!item.revoked_at"
                :aria-label="$t('agent_tokens.revoke_button')" :title="$t('agent_tokens.revoke_button')"
                data-testid="agent-token-revoke" @click="confirmRevoke(item)" :disabled="isLoading">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </section>

        <v-alert v-if="oauthSupported && connector && connector.name && connector.url" type="info" outlined dense class="mt-8 mb-0"
          data-testid="agent-tokens-oauth-hint">
          {{ $t('agent_tokens.oauth_connector_hint', { name: connector.name, url: connector.url }) }}
        </v-alert>

        <section v-if="oauthSupported" class="agent-tokens__list">
          <h3 class="agent-tokens__title">
            {{ $t('agent_tokens.oauth_title') }}
            <span v-if="oauthTokens.length" class="agent-tokens__count">{{ oauthTokens.length }}</span>
          </h3>
          <v-data-table
            :headers="oauthHeaders"
            :items="oauthTokens"
            :loading="isLoading"
            dense
            hide-default-footer
            disable-sort
            :items-per-page="-1"
            class="agent-tokens__table"
            data-testid="agent-tokens-oauth-table"
          >
            <template v-slot:no-data>
              <div class="agent-tokens__empty">
                <v-icon color="grey lighten-1">mdi-connection</v-icon>
                <span>{{ $t('agent_tokens.oauth_empty') }}</span>
              </div>
            </template>
            <template v-slot:item.client="{ item }">
              {{ item.client && item.client.name ? item.client.name : item.name }}
            </template>
            <template v-slot:item.levels="{ item }">
              <div class="d-flex flex-wrap">
                <v-chip v-for="entry in tokenLevelChips(item)" :key="entry.area" x-small
                  :color="entry.write ? 'warning' : undefined" :outlined="!entry.write" class="mr-1 mb-1">
                  {{ entry.text }}
                </v-chip>
              </div>
            </template>
            <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
            <template v-slot:item.last_used_at="{ item }">
              {{ item.last_used_at ? formatDate(item.last_used_at) : $t('agent_tokens.never_used') }}
            </template>
            <template v-slot:item.revoked_at="{ item }">
              <v-chip x-small color="error" v-if="item.revoked_at">{{ $t('agent_tokens.revoked') }}</v-chip>
              <v-chip x-small color="success" v-else>{{ $t('agent_tokens.active') }}</v-chip>
            </template>
            <template v-slot:item.user="{ item }">
              {{ item.user && item.user.name ? item.user.name : '—' }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn x-small icon color="error" v-if="!item.revoked_at" :aria-label="$t('agent_tokens.oauth_revoke')"
                :title="$t('agent_tokens.oauth_revoke')" @click="confirmRevoke(item)" :disabled="isLoading">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </section>

        <!-- Выпуск ключа -->
        <v-dialog v-model="createDialog" max-width="620" persistent scrollable>
          <v-card>
            <v-card-title>{{ $t('agent_tokens.create_title') }}</v-card-title>
            <!-- scrollable-диалог режет всплывающую метку outlined-поля: v-card-text без верхнего отступа -->
            <v-card-text class="pt-3">
              <v-text-field v-model="newTokenName" :label="$t('agent_tokens.name_label')"
                outlined dense :rules="[v => !!v || $t('agent_tokens.name_required')]" autofocus />

              <template v-if="levels">
                <div class="subtitle-2 mb-1">{{ $t('agent_tokens.levels_title') }}</div>
                <p class="caption grey--text text--darken-1 mb-2">{{ $t('agent_tokens.levels_hint') }}</p>
                <div v-for="area in areas" :key="area" class="mb-1" :data-testid="`agent-token-area-${area}`">
                  <v-select
                    :value="levels[area]"
                    :items="levelOptions(area)"
                    :label="$t(areaKeys[area])"
                    :hint="areaHint(area)"
                    :disabled="levelOptions(area).length < 2"
                    persistent-hint
                    outlined dense
                    @change="setLevel(area, $event)"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-subtitle class="text-wrap">{{ item.hint }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </div>

                <v-alert v-for="warning in riskWarnings" :key="`risk-${warning.area}`" :type="warning.type"
                  dense text class="mt-2 mb-0" :data-testid="`agent-token-risk-${warning.area}`">
                  {{ warning.text }}
                </v-alert>
                <v-alert v-if="crmMoneyDirectWarning" type="error" dense prominent class="mt-2 mb-0"
                  data-testid="agent-token-risk-crm-money-direct">
                  {{ $t('agent_tokens.risk_crm_money_direct') }}
                </v-alert>
                <v-alert v-if="allLevelsNone" type="info" dense text class="mt-2 mb-0">
                  {{ $t('agent_tokens.levels_all_none') }}
                </v-alert>
              </template>

              <p class="caption grey--text mt-3 mb-0">{{ $t('agent_tokens.create_hint') }}</p>
              <!-- Выбор агента и передача ему ПДн — ответственность компании (решение USER, FT-184, 2026-10-02) -->
              <p class="caption grey--text mt-1 mb-0" data-testid="agent-token-data-notice">
                {{ $t('agent_tokens.data_notice') }}
              </p>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="createDialog = false" :disabled="isCreating">{{ $t('custom.cancel') }}</v-btn>
              <v-btn color="primary" depressed :loading="isCreating" :disabled="!newTokenName || allLevelsNone"
                @click="createToken">{{ $t('custom.create') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Ключ показывается ОДИН раз -->
        <v-dialog v-model="plainDialog" max-width="600" persistent>
          <v-card>
            <v-card-title>{{ $t('agent_tokens.plain_title') }}</v-card-title>
            <v-card-text>
              <v-alert type="warning" outlined dense class="mb-3">
                {{ $t('agent_tokens.plain_warning') }}
              </v-alert>
              <v-text-field :value="plainTokenShown" readonly outlined dense autocomplete="off" spellcheck="false">
                <template v-slot:append>
                  <v-btn icon small :aria-label="$t('agent_tokens.key_copy')" :title="$t('agent_tokens.key_copy')"
                    @click="copyToken">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
              <v-text-field :value="mcpUrl" :label="$t('agent_tokens.mcp_address')" readonly outlined dense
                hide-details class="mb-5" autocomplete="off" spellcheck="false">
                <template v-slot:append>
                  <v-btn icon small :aria-label="$t('agent_tokens.mcp_address_copy')" :title="$t('agent_tokens.mcp_address_copy')"
                    @click="copyText(mcpUrl, 'agent_tokens.mcp_address_copied')">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
              <!-- FT-189 REQ-09: готовая команда CLI — ключ и адрес региона внутри (DEC-01) -->
              <v-text-field v-if="cliCommand" :value="cliCommand" :label="$t('agent_tokens.cli_command')" readonly outlined dense
                autocomplete="off" spellcheck="false" :hint="$t('agent_tokens.cli_hint')" persistent-hint
                data-testid="agent-tokens-cli-command">
                <template v-slot:append>
                  <v-btn icon small :aria-label="$t('agent_tokens.cli_command_copy')" :title="$t('agent_tokens.cli_command_copy')"
                    @click="copyText(cliCommand, 'agent_tokens.cli_command_copied')">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" depressed @click="plainDialog = false">
                {{ $t('agent_tokens.plain_close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Подтверждение отзыва -->
        <v-dialog v-model="revokeDialog" max-width="400">
          <v-card>
            <v-card-title>{{ $t(tokenToRevoke && tokenToRevoke.kind === 'oauth' ? 'agent_tokens.oauth_revoke_title' : 'agent_tokens.revoke_title') }}</v-card-title>
            <v-card-text>
              {{ $t(tokenToRevoke && tokenToRevoke.kind === 'oauth' ? 'agent_tokens.oauth_revoke_confirm' : 'agent_tokens.revoke_confirm',
                { name: tokenToRevoke && (tokenToRevoke.client ? tokenToRevoke.client.name : tokenToRevoke.name) }) }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="revokeDialog = false">{{ $t('custom.cancel') }}</v-btn>
              <v-btn color="error" depressed :loading="isLoading" @click="revokeToken">
                {{ $t(tokenToRevoke && tokenToRevoke.kind === 'oauth' ? 'agent_tokens.oauth_revoke' : 'agent_tokens.revoke_button') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// FT-155 REQ-10 — персональные ключи агента (MCP) в профиле сотрудника. Образец —
// SitesBuilderTokens.vue, но гейт другой: доступность услуги отдаёт сам API
// (GET /api/v1/agent_access_tokens → feature_enabled), а не флаг плана из store —
// у услуги нет триала, и сервер уже знает ответ.
// Plain-ключ приходит один раз в ответе create и после закрытия окна не хранится.
//
// FT-184 REQ-15 / SC-01: ступени ключа по областям. Потолок (`ceiling`), умолчание
// (`default_levels`) и `can_issue` отдаёт GET; варианты выше потолка не
// показываются вовсе (guest с потолком read записи не видит). Рекомендация —
// только подсказка: при записи в money рядом «рекомендуем: одобрение», в crm —
// «рекомендуем: предпросмотр»; выбор пользователя не подменяется. Риск выбранной
// ступени — отдельным предупреждением.
import axios from 'axios';
import { copyToClipboard } from '@/utils/clipboard';
import { CLI_PACKAGE } from '@/utils/agentNotice';
import moment from 'moment';

export const API_PATH = '/api/v1/agent_access_tokens';

// Код ошибки API → ключ локали. Неизвестный код → общее сообщение.
export const ERROR_KEYS = {
  feature_not_included: 'agent_tokens.feature_not_included',
  role_not_allowed: 'agent_tokens.role_not_allowed',
  too_many_tokens: 'agent_tokens.too_many_tokens',
  no_branch: 'agent_tokens.no_branch',
  scope_above_ceiling: 'agent_tokens.scope_above_ceiling',
  invalid_levels: 'agent_tokens.invalid_levels',
};

// Зеркало AgentAccessToken::SCOPE_AREAS / SCOPE_LEVELS (rentalman-api). Порядок
// ступеней — от строгой к свободной, по нему сравнивается потолок.
export const AREAS = ['crm', 'fleet', 'money', 'staff'];
export const LEVELS = ['none', 'read', 'approve', 'preview', 'direct'];
export const WRITE_LEVELS = ['approve', 'preview', 'direct'];
export const RECOMMENDED = { money: 'approve', crm: 'preview' };

export const AREA_KEYS = {
  crm: 'agent_tokens.area_crm',
  fleet: 'agent_tokens.area_fleet',
  money: 'agent_tokens.area_money',
  staff: 'agent_tokens.area_staff',
};
export const AREA_HINT_KEYS = {
  crm: 'agent_tokens.area_hint_crm',
  fleet: 'agent_tokens.area_hint_fleet',
  money: 'agent_tokens.area_hint_money',
  staff: 'agent_tokens.area_hint_staff',
};
export const LEVEL_KEYS = {
  none: 'agent_tokens.level_none',
  read: 'agent_tokens.level_read',
  approve: 'agent_tokens.level_approve',
  preview: 'agent_tokens.level_preview',
  direct: 'agent_tokens.level_direct',
};
export const LEVEL_HINT_KEYS = {
  none: 'agent_tokens.level_hint_none',
  read: 'agent_tokens.level_hint_read',
  approve: 'agent_tokens.level_hint_approve',
  preview: 'agent_tokens.level_hint_preview',
  direct: 'agent_tokens.level_hint_direct',
};
export const RECOMMEND_KEYS = {
  money: 'agent_tokens.recommend_money',
  crm: 'agent_tokens.recommend_crm',
};
// Риск ступени записи; тип алерта растёт к «сразу».
export const RISK = {
  approve: { type: 'info', key: 'agent_tokens.risk_approve' },
  preview: { type: 'warning', key: 'agent_tokens.risk_preview' },
  direct: { type: 'error', key: 'agent_tokens.risk_direct' },
};

// Статья справки о подключении AI-агентов: ссылка «Как подключить» и строка о CLI.
export const HELP_PATH = '/nastroyki/ai-agenty';

// Адрес MCP-сервера: сервер отдаёт адрес региона компании (regions.yml mcp_url: RU — api.rentprog.ru/mcp,
// EU — api.rentprog.com/mcp; вне production сервер его не отдаёт). Нет его в ответе (development, стенд, регион
// без адреса, API до выката) — API, к которому подключено приложение, + /mcp.
export function mcpUrlFor(serverMcpUrl, apiUrl) {
  if (serverMcpUrl) return serverMcpUrl;
  return `${String(apiUrl || '').replace(/\/+$/, '')}/mcp`;
}

// Команда подключения из окна ключа: ключ и адрес региона внутри (DEC-01 FT-189); без ключа — пусто.
// Команду вставляют в терминал как есть: ключ (rpa_ + base64url) и адрес обязаны состоять из безопасных для
// оболочки символов, иначе команды нет — лучше без неё, чем с подстановкой оболочки (кавычки ломаются в cmd).
const CLI_SAFE_KEY = /^[A-Za-z0-9_-]+$/;
const CLI_SAFE_URL = /^https?:\/\/[A-Za-z0-9.:[\]_~/-]+$/;
export function cliLoginCommand(key, mcpUrl) {
  if (!key || !mcpUrl || !CLI_SAFE_KEY.test(key) || !CLI_SAFE_URL.test(mcpUrl)) return '';
  return `npx -y ${CLI_PACKAGE} login ${key} --url ${mcpUrl}`;
}

export function levelRank(level) {
  return LEVELS.indexOf(level);
}

export function isWriteLevel(level) {
  return WRITE_LEVELS.includes(level);
}

// Ступени до потолка включительно. Неизвестный потолок — только «нет».
export function levelsUpTo(ceilingLevel) {
  const top = levelRank(ceilingLevel);
  return top < 0 ? ['none'] : LEVELS.slice(0, top + 1);
}

export function clampLevelsToCeiling(levels, ceiling) {
  const result = {};
  AREAS.forEach((area) => {
    const level = levels && LEVELS.includes(levels[area]) ? levels[area] : 'none';
    const top = ceiling && ceiling[area] ? ceiling[area] : 'none';
    result[area] = levelRank(level) > levelRank(top) ? top : level;
  });
  return result;
}

export function areAllLevelsNone(levels) {
  return !!levels && AREAS.every((area) => !levels[area] || levels[area] === 'none');
}

export default {
  name: 'AgentAccessTokens',
  data() {
    return {
      featureEnabled: null,
      serverMcpUrl: null,
      // Адрес показывается после ответа сервера: до него запасной адрес мелькнул бы и сменился.
      mcpLoaded: false,
      canIssue: null,
      ceiling: null,
      defaultLevels: null,
      levels: null,
      tokens: [],
      oauthSupported: false,
      connector: null,
      oauthTokens: [],
      isLoading: false,
      isCreating: false,
      createDialog: false,
      plainDialog: false,
      revokeDialog: false,
      newTokenName: '',
      plainTokenShown: '',
      tokenToRevoke: null,
      areas: AREAS,
      areaKeys: AREA_KEYS,
    };
  },
  watch: {
    plainDialog(visible) {
      if (!visible) {
        this.plainTokenShown = '';
      }
    },
  },
  computed: {
    activeTokensCount() {
      return this.tokens.filter((token) => !token.revoked_at && !this.isExpired(token)).length;
    },
    mcpUrl() {
      return mcpUrlFor(this.serverMcpUrl, this.$store.getters.getApiUrl);
    },
    cliCommand() {
      return cliLoginCommand(this.plainTokenShown, this.mcpUrl);
    },
    helpUrl() {
      return this.docsLink(HELP_PATH);
    },
    // Предупреждения о риске выбранных ступеней записи, по одному на область.
    riskWarnings() {
      if (!this.levels) return [];
      return AREAS.filter((area) => isWriteLevel(this.levels[area])).map((area) => {
        const risk = RISK[this.levels[area]];
        return {
          area,
          type: risk.type,
          text: this.$t(risk.key, { area: this.$t(AREA_KEYS[area]) }),
        };
      });
    },
    // Отдельное предупреждение: CRM ≥ чтение + деньги «сразу» (бриф DEC-10).
    crmMoneyDirectWarning() {
      if (!this.levels) return false;
      return levelRank(this.levels.crm) >= levelRank('read') && this.levels.money === 'direct';
    },
    allLevelsNone() {
      return areAllLevelsNone(this.levels);
    },
    headers() {
      return [
        { text: this.$t('agent_tokens.name'), value: 'name' },
        { text: this.$t('agent_tokens.levels_column'), value: 'levels', sortable: false },
        { text: this.$t('agent_tokens.last4'), value: 'token_last4', sortable: false },
        { text: this.$t('agent_tokens.owner'), value: 'user', sortable: false },
        { text: this.$t('agent_tokens.status'), value: 'revoked_at', sortable: false },
        { text: this.$t('agent_tokens.expires_at'), value: 'expires_at' },
        { text: this.$t('agent_tokens.last_used_at'), value: 'last_used_at' },
        { text: '', value: 'actions', sortable: false },
      ];
    },
    oauthHeaders() {
      const headers = [
        { text: this.$t('agent_tokens.oauth_client'), value: 'client' },
        { text: this.$t('agent_tokens.oauth_host'), value: 'redirect_host' },
        { text: this.$t('agent_tokens.levels_column'), value: 'levels', sortable: false },
        { text: this.$t('agent_tokens.oauth_created_at'), value: 'created_at' },
        { text: this.$t('agent_tokens.last_used_at'), value: 'last_used_at' },
        { text: this.$t('agent_tokens.status'), value: 'revoked_at', sortable: false },
      ];
      const currentUser = this.$store.getters.getCurrentUser;
      if (currentUser && currentUser.role === 'superadmin') {
        headers.push({ text: this.$t('agent_tokens.owner'), value: 'user', sortable: false });
      }
      headers.push({ text: '', value: 'actions', sortable: false });
      return headers;
    },
  },
  mounted() {
    this.loadTokens();
  },
  methods: {
    formatDate(iso) {
      return moment(iso).format('YYYY-MM-DD HH:mm');
    },
    isExpired(token) {
      return !!token.expires_at && moment(token.expires_at).isBefore(moment());
    },
    firstError(err) {
      const errors = err && err.response && err.response.data && err.response.data.errors;
      return (Array.isArray(errors) && errors[0]) || null;
    },
    errorMessageFor(err, fallbackKey) {
      const error = this.firstError(err);
      const code = error && error.code;
      if (code === 'scope_above_ceiling') {
        const areas = (Array.isArray(error.areas) ? error.areas : [])
          .filter((area) => AREA_KEYS[area])
          .map((area) => this.$t(AREA_KEYS[area]))
          .join(', ');
        return this.$t(ERROR_KEYS.scope_above_ceiling, { areas });
      }
      return this.$t(ERROR_KEYS[code] || fallbackKey);
    },
    // Ступени в пределах потолка: всё, что выше, опускается до потолка.
    clampToCeiling(levels) {
      if (!this.ceiling) return levels;
      return clampLevelsToCeiling(levels, this.ceiling);
    },
    levelOptions(area) {
      const top = this.ceiling ? this.ceiling[area] : 'none';
      return levelsUpTo(top).map((level) => ({
        value: level,
        text: this.$t(LEVEL_KEYS[level]),
        hint: this.$t(LEVEL_HINT_KEYS[level]),
      }));
    },
    areaHint(area) {
      if (this.levelOptions(area).length < 2) return this.$t('agent_tokens.area_unavailable');
      const recommended = RECOMMENDED[area];
      if (recommended && this.levels && isWriteLevel(this.levels[area]) && this.ceiling
        && levelRank(recommended) <= levelRank(this.ceiling[area])) {
        return this.$t(RECOMMEND_KEYS[area]);
      }
      return this.$t(AREA_HINT_KEYS[area]);
    },
    // Выбор ступени — ровно тот, что выбрал пользователь: рекомендация только
    // подсказывает (areaHint), выбор не подменяет (fix round 1 STEP-06b).
    setLevel(area, value) {
      if (!this.levels) return;
      this.levels = { ...this.levels, [area]: value };
    },
    tokenLevelChips(token) {
      const levels = token.levels || {};
      return AREAS.filter((area) => levels[area] && levels[area] !== 'none' && LEVEL_KEYS[levels[area]])
        .map((area) => ({
          area,
          write: isWriteLevel(levels[area]),
          text: `${this.$t(AREA_KEYS[area])}: ${this.$t(LEVEL_KEYS[levels[area]])}`,
        }));
    },
    async loadTokens() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(API_PATH);
        this.featureEnabled = data.feature_enabled !== false;
        this.serverMcpUrl = data.mcp_url || null;
        this.canIssue = data.can_issue !== false;
        this.ceiling = data.ceiling || null;
        this.defaultLevels = data.default_levels || null;
        const returnedTokens = data.tokens || [];
        const hasConnector = Object.prototype.hasOwnProperty.call(data, 'connector');
        this.connector = hasConnector ? data.connector : null;
        this.oauthTokens = returnedTokens.filter((token) => token && token.kind === 'oauth');
        this.tokens = returnedTokens.filter((token) => !token || token.kind !== 'oauth');
        this.oauthSupported = hasConnector || this.oauthTokens.length > 0;
      } catch (err) {
        this.$notify(this.$t('agent_tokens.load_error'), '', 'error');
      } finally {
        this.mcpLoaded = true;
        this.isLoading = false;
      }
    },
    openCreateDialog() {
      if (this.canIssue === false) return;
      this.newTokenName = '';
      this.levels = this.ceiling ? this.clampToCeiling(this.defaultLevels) : null;
      this.createDialog = true;
    },
    async createToken() {
      if (!this.newTokenName || this.allLevelsNone) return;
      this.isCreating = true;
      try {
        const body = { name: this.newTokenName };
        if (this.levels) body.levels = { ...this.levels };
        const { data } = await axios.post(API_PATH, body);
        this.tokens.unshift(data.token);
        this.plainTokenShown = data.plain_token;
        this.createDialog = false;
        this.plainDialog = true;
      } catch (err) {
        // Потолок мог сузиться с момента загрузки (роль, CRM) — сервер отдаёт
        // актуальный; ступени опускаются до него, окно остаётся открытым.
        const error = this.firstError(err);
        if (error && error.code === 'scope_above_ceiling' && error.ceiling && this.levels) {
          this.ceiling = error.ceiling;
          this.levels = this.clampToCeiling(this.levels);
        }
        this.$notify(this.errorMessageFor(err, 'agent_tokens.create_error'), '', 'error');
      } finally {
        this.isCreating = false;
      }
    },
    confirmRevoke(token) {
      this.tokenToRevoke = token;
      this.revokeDialog = true;
    },
    async revokeToken() {
      if (!this.tokenToRevoke) return;
      const oauth = this.tokenToRevoke.kind === 'oauth';
      this.isLoading = true;
      try {
        await axios.delete(`${API_PATH}/${this.tokenToRevoke.id}`);
        await this.loadTokens();
        this.revokeDialog = false;
        this.$notify(this.$t(oauth ? 'agent_tokens.oauth_revoked_success' : 'agent_tokens.revoked_success'), '', 'success');
      } catch (err) {
        this.$notify(this.errorMessageFor(err, 'agent_tokens.revoke_error'), '', 'error');
      } finally {
        this.isLoading = false;
        this.tokenToRevoke = null;
      }
    },
    async copyToken() {
      await this.copyText(this.plainTokenShown);
    },
    async copyText(value, successKey = 'agent_tokens.copied') {
      try {
        await copyToClipboard(value);
        this.$notify(this.$t(successKey), '', 'success');
      } catch (err) {
        this.$notify(this.$t('agent_tokens.copy_failed'), '', 'warning');
      }
    },
  },
};
</script>

<style scoped>
/* Ритм вкладки: шаг 4px; внутри группы 12–16, между группами 32. */
.agent-tokens {
  padding-top: 8px;
}
.agent-tokens__head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}
.agent-tokens__lead {
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.7);
}
.agent-tokens__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.87);
}
.agent-tokens__count {
  min-width: 22px;
  padding: 0 7px;
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.06);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
}
/* Подключение — справка к действию: тонированная область без тени, не карточка. */
.agent-tokens__connect {
  margin-top: 32px;
  padding: 16px 20px 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: rgba(25, 118, 210, 0.04);
}
.agent-tokens__connect-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;
}
.agent-tokens__address {
  flex: 1 1 280px;
  min-width: 0;
  max-width: 520px;
}
.agent-tokens__copy {
  margin-top: -4px;
}
.agent-tokens__help {
  text-decoration: none;
}
.agent-tokens__help:hover,
.agent-tokens__help:focus-visible {
  text-decoration: underline;
}
.agent-tokens__list {
  margin-top: 32px;
}
.agent-tokens__table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}
.agent-tokens__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 0;
  color: rgba(0, 0, 0, 0.6);
}

.theme--dark .agent-tokens__lead { color: rgba(255, 255, 255, 0.7); }
.theme--dark .agent-tokens__title { color: rgba(255, 255, 255, 0.87); }
.theme--dark .agent-tokens__count { background: rgba(255, 255, 255, 0.12); color: rgba(255, 255, 255, 0.7); }
.theme--dark .agent-tokens__connect { border-color: rgba(255, 255, 255, 0.12); background: rgba(144, 202, 249, 0.06); }
.theme--dark .agent-tokens__table { border-color: rgba(255, 255, 255, 0.12); }
.theme--dark .agent-tokens__empty { color: rgba(255, 255, 255, 0.6); }
</style>
